import { useChat } from "@/features/chat"
import { useLogger, ANALYTICS_EVENTS } from "@/features/analytics"

import { advisorsList } from "../config/advisors"
import { sectionCopy } from "../config/sections"
import { getFirstName, getProfession } from "../lib/advisor-helpers"
import { RatingStars } from "./rating-stars"
import type { Advisor } from "../types"

import Decoration from "@/assets/img/lpr4/advisor/otd/advisor-otd-bg.avif"

// Picked once per page load, mirroring the source (module-level random pick).
const advisor = advisorsList[Math.floor(Math.random() * advisorsList.length)]

interface AdvisorOfTheDayProps {
  onClick?: (advisor: Advisor) => void
}

export function AdvisorOfTheDay({ onClick }: AdvisorOfTheDayProps) {
  const { user } = useChat()
  const { trackAppyCntEvent } = useLogger()

  const handleClick = () => {
    onClick?.(advisor)
    trackAppyCntEvent(ANALYTICS_EVENTS.CHAT_WITH_ADVISOR_OF_THE_DAY, undefined, {
      user: { uid: user?.uid, name: user?.displayName, host: window.location.host },
      advisor: {
        name: advisor.name,
        id: advisor.assistantId,
        chat_type: "Chat with Advisor of the day",
      },
    })
  }

  return (
    <section className="w-full bg-dark-base pt-8">
      <h2 className="text-center font-display text-2xl font-medium text-purple-light">
        {sectionCopy.advisorOTD.title}
      </h2>

      <div className="relative">
        <div className="relative mx-auto mt-4 flex items-center justify-center px-4">
          <img src={Decoration} alt="" className="w-full" />
          <img
            src={advisor.photo}
            alt={advisor.name}
            className="absolute left-1/2 top-[15%] aspect-square w-[30%] -translate-x-1/2 rounded-full object-cover object-top"
          />

          <div className="absolute left-1/2 top-[57%] flex -translate-x-1/2 flex-col items-center justify-center">
            <p className="text-center font-display text-xl font-medium text-white">
              {advisor.name}
            </p>
            <p className="text-center font-body text-sm text-purple-light/70">
              {getProfession(advisor.exp)}
            </p>
            <div className="mt-3 flex flex-col items-center gap-1">
              <RatingStars rating={advisor.rating} className="h-4 w-20 text-gold" />
              <div className="flex items-center gap-1">
                <span className="font-body text-sm font-medium text-gold">{advisor.rating}</span>
                <span className="font-body text-sm text-purple-light/70">{advisor.reviews}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-4 px-4 text-center font-body text-base text-purple-light/70">
        {sectionCopy.advisorOTD.subtitle}
      </p>

      <button
        type="button"
        onClick={handleClick}
        className="mx-auto mt-4 block h-14 w-[calc(100%-40px)] rounded-[30px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
      >
        Chat with {getFirstName(advisor.name)}
      </button>
    </section>
  )
}
